import { type FC, type MutableRefObject, useCallback, useMemo } from 'react';
import { RiSquareFill } from 'react-icons/ri';
import { type StudyApiId } from '@/model/Study.model';
import { _isNil, _notNil } from '@/littledash';
import {
  type GroupMap,
  type SampleTypeMap,
  type MetaField,
  type ReservedSample,
  type ReservedSampleApiId,
  type SampleDefinitionMap,
} from './SampleNames.model';
import { renderTimepoint, getMetaValue } from './SampleNames.utils';
import ReservedSampleStatusChip from './ReservedSampleStatusChip';
import { type GroupApiId, type TreatmentGroup } from '@/model/TreatmentGroup.model';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { IndeterminateCheckbox } from '@/components/UI/Table/Columns/Selector';
import { RouteService } from '@/support/RouteService';
import styles from './SampleNamesTable.module.scss';
import type { UserTableViewSettings } from '@/model/UserTableView.model';

const GroupChip: FC<{ group: TreatmentGroup | undefined }> = ({ group }) => {
  if (_isNil(group)) {
    return null;
  }
  return (
    <div className="flex flex-row items-center" style={{ gap: '0.25rem' }}>
      {_notNil(group.color) && <RiSquareFill color={group.color} />}
      <span>{group.name}</span>
    </div>
  );
};

/**
 * Flat (ungrouped) view of reserved samples for a study, rendered as a paginated table.
 *
 * Two fetch modes:
 * - **GET mode** (no `reservedSampleApiIds`): loads the full study list via GET.
 * - **POST mode** (`reservedSampleApiIds` provided): fetches only the specified samples via
 *   POST list-by-ids, keeping all APITable features (search, column visibility, pagination).
 */
interface ReservedSamplesFlatTableProps {
  studyApiId: StudyApiId;
  groupMap: GroupMap;
  sampleTypeMap: SampleTypeMap;
  metaFields: Array<MetaField>;
  sampleDefinitionMap: SampleDefinitionMap;
  selectedReservedSampleIds: Set<ReservedSampleApiId>;
  toggleReservedSampleIds: (ids: Array<ReservedSampleApiId>) => void;
  /** When provided, fetches only these samples via POST list-by-ids instead of GET all. */
  reservedSampleApiIds?: Array<ReservedSampleApiId>;
  filterCollected?: boolean;
  hideBorder?: boolean;
  refetch?: number | string;
  onSamplesLoaded?: (samples: Array<ReservedSample>) => void;
  columnVisibility?: Record<string, boolean>;
  getSettingsRef?: MutableRefObject<(() => UserTableViewSettings) | null>;
  activeSettingsOverride?: UserTableViewSettings;
}

const ReservedSamplesFlatTable: FC<ReservedSamplesFlatTableProps> = ({
  studyApiId,
  groupMap,
  sampleTypeMap,
  metaFields,
  sampleDefinitionMap,
  selectedReservedSampleIds,
  toggleReservedSampleIds,
  reservedSampleApiIds,
  filterCollected = false,
  hideBorder = true,
  refetch,
  onSamplesLoaded,
  columnVisibility,
  getSettingsRef,
  activeSettingsOverride,
}) => {
  const columns = useMemo<Array<TanStackColumnDef<ReservedSample>>>(() => {
    const staticColumns: Array<TanStackColumnDef<ReservedSample>> = [
      {
        id: 'selection',
        minSize: 40,
        maxSize: 40,
        enableResizing: false,
        enableSorting: false,
        meta: { className: 'ui__selector-column' },
        header: ({ table }) => {
          const selectableIds = table
            .getRowModel()
            .rows.filter(
              (r) =>
                r.original.status !== 'collected' &&
                r.original.status !== 'cancelled' &&
                _notNil(r.original.animal_api_id)
            )
            .map((r) => r.original.api_id);
          const allSelected =
            selectableIds.length > 0 && selectableIds.every((id) => selectedReservedSampleIds.has(id));
          const someSelected = !allSelected && selectableIds.some((id) => selectedReservedSampleIds.has(id));
          return (
            <div data-test-element="select-all-header">
              <IndeterminateCheckbox
                checked={allSelected}
                indeterminate={someSelected}
                disabled={selectableIds.length === 0}
                onChange={() => toggleReservedSampleIds(selectableIds)}
                title="Toggle All Rows Selected"
              />
            </div>
          );
        },
        cell: ({ row }) => {
          const disabledReason =
            row.original.status === 'collected'
              ? 'Sample already collected'
              : row.original.status === 'cancelled'
                ? 'Sample cancelled'
                : _isNil(row.original.animal_api_id)
                  ? 'Must be assigned to a registered animal'
                  : undefined;
          return (
            <span
              data-tooltip-id="global-tooltip-id"
              data-tooltip-content={disabledReason}
              data-tooltip-hidden={_isNil(disabledReason)}
            >
              <IndeterminateCheckbox
                checked={selectedReservedSampleIds.has(row.original.api_id)}
                disabled={_notNil(disabledReason)}
                aria-label="Select sample"
                onChange={() => toggleReservedSampleIds([row.original.api_id])}
              />
            </span>
          );
        },
      },
      {
        id: 'name',
        header: 'Sample Name',
        accessorKey: 'name',
        enableSorting: false,
        enableResizing: true,
        size: 200,
      },
      {
        id: 'sampleDefinition',
        header: 'Sample Definition',
        accessorFn: (row) => row.sample_definition_api_id,
        cell: ({ row }) => {
          const defApiId = row.original.sample_definition_api_id;
          if (_isNil(defApiId)) {
            return '';
          }
          return sampleDefinitionMap.get(defApiId)?.name ?? defApiId;
        },
        enableSorting: false,
        enableResizing: true,
        size: 200,
      },
      {
        id: 'group',
        header: 'Study Group',
        accessorFn: (row) => row.study_group_api_id,
        cell: ({ row }) => {
          const group = _notNil(row.original.study_group_api_id)
            ? groupMap.get(row.original.study_group_api_id as GroupApiId)
            : undefined;
          return <GroupChip group={group} />;
        },
        enableSorting: false,
        enableResizing: true,
        size: 160,
      },
      {
        id: 'plannedAnimal',
        header: 'Planned Animal',
        accessorFn: (row) => row.planned_animal_id ?? '',
        enableSorting: false,
        enableResizing: true,
        size: 140,
      },
      {
        id: 'animal',
        header: 'Animal',
        accessorFn: (row) => row.animal_name ?? '',
        enableSorting: false,
        enableResizing: true,
        size: 140,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => row.status,
        cell: ({ row }) => <ReservedSampleStatusChip status={row.original.status} />,
        enableSorting: false,
        enableResizing: true,
        size: 120,
      },
      {
        id: 'timepoint',
        header: 'Timepoint',
        accessorFn: (row) => row.timepoint ?? '',
        cell: ({ row }) => (_notNil(row.original.timepoint) ? renderTimepoint(row.original.timepoint) : ''),
        enableSorting: false,
        enableResizing: true,
        size: 160,
      },
      {
        id: 'sampleType',
        header: 'Sample Type',
        accessorFn: (row) => {
          const defApiId = row.sample_definition_api_id;
          if (_isNil(defApiId)) {
            return '';
          }
          const sampleDef = sampleDefinitionMap.get(defApiId);
          return _notNil(sampleDef) ? (sampleTypeMap.get(sampleDef.sample_type) ?? sampleDef.sample_type) : '';
        },
        enableSorting: false,
        enableResizing: true,
        size: 140,
      },
    ];

    const metaColumns = metaFields.map<TanStackColumnDef<ReservedSample>>((field) => ({
      id: `meta_${field.apiId}`,
      header: field.title,
      accessorFn: (row) => {
        const defApiId = row.sample_definition_api_id;
        if (_isNil(defApiId)) {
          return '';
        }
        const sampleDef = sampleDefinitionMap.get(defApiId);
        return _notNil(sampleDef) ? getMetaValue(sampleDef.metadata ?? [], field.apiId) : '';
      },
      enableSorting: false,
      enableResizing: true,
      size: 160,
    }));

    return _notNil(columnVisibility)
      ? [...staticColumns, ...metaColumns].filter((col) =>
          col.id.startsWith('meta_') ? columnVisibility[col.id] === true : columnVisibility[col.id] !== false
        )
      : [...staticColumns, ...metaColumns];
  }, [
    sampleDefinitionMap,
    groupMap,
    sampleTypeMap,
    metaFields,
    selectedReservedSampleIds,
    toggleReservedSampleIds,
    columnVisibility,
  ]);

  const handleFilterData = useCallback(
    (data: Array<ReservedSample>) => (filterCollected ? data.filter((s) => s.status !== 'collected') : data),
    [filterCollected]
  );

  const idsProvided = _notNil(reservedSampleApiIds);
  const hasIds = idsProvided && reservedSampleApiIds.length > 0;

  const apiInfo = useMemo(
    () =>
      hasIds
        ? {
            type: 'internalApiPost' as const,
            route: RouteService.api({
              endpoint: 'POST /api/v1/studies/{studyApiId}/planned/samples/reserved-samples/list',
              path: { studyApiId },
            }).url,
            body: { reserved_sample_api_ids: reservedSampleApiIds },
          }
        : {
            type: 'internalApi' as const,
            route: RouteService.api({
              endpoint: 'GET /api/v1/studies/{studyApiId}/planned/samples/reserved-samples',
              path: { studyApiId },
            }).url,
          },
    [studyApiId, hasIds, reservedSampleApiIds]
  );

  if (idsProvided && !hasIds) {
    return (
      <div
        className={hideBorder ? styles.FlatTableContainer : undefined}
        data-test-component="ReservedSamplesFlatTable"
        data-test-element="container"
      >
        <div className="flex flex-column items-center bg-light-gray ma4 pa4">
          <div className="f4 lh-copy">No Sample Names</div>
          <div className="mv4">No samples selected</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={hideBorder ? styles.FlatTableContainer : undefined}
      data-test-component="ReservedSamplesFlatTable"
      data-test-element="container"
    >
      <APITable<ReservedSample>
        columns={columns}
        apiInfo={apiInfo}
        filterData={filterCollected ? handleFilterData : undefined}
        refetch={refetch}
        onFetchTableData={onSamplesLoaded}
        reduxTableName="reservedSamples"
        hideShow={_notNil(columnVisibility)}
        searchPlaceholderText="Search samples"
        searchQueryByOptions={[
          { value: 'name', display: 'Name' },
          { value: 'animal', display: 'Animal' },
          { value: 'planned_animal', display: 'Planned Animal' },
        ]}
        hideFilter
        defaultPageSize={1000}
        testIdPrefix="reserved-samples-flat"
        getSettingsRef={getSettingsRef}
        activeSettingsOverride={activeSettingsOverride}
      />
    </div>
  );
};

export default ReservedSamplesFlatTable;
